/**
 * DOCUMENTATION:
 * Component: CssScssPreprocessor
 * Purpose: Educational page detailing how to use the SCSS preprocessor to write modular, programmatic CSS.
 * * * Note for Junior Developers:
 * - CSS is static. SCSS introduces programming logic (Variables, Functions, Loops) to your styling.
 * - Golden Rule of Nesting: The Inception Rule. Never nest more than 3 levels deep.
 * If you go deeper, your specificity becomes a nightmare and your compiled CSS becomes bloated.
 * - Always use `@use` instead of the deprecated `@import` for modular architecture.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function CssScssPreprocessor() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core SCSS Features
    const scssFeatures = useMemo(() => [
        { feature: "Variables ($)", purpose: "Single Source of Truth", description: "Store colors, fonts, or spacing values. Update it once, and it updates everywhere in your project." },
        { feature: "Nesting", purpose: "Visual Hierarchy", description: "Nest CSS selectors in a way that follows the same visual hierarchy of your HTML. Prevents repeating parent class names." },
        { feature: "Mixins (@mixin)", purpose: "Reusable Logic Blocks", description: "Create groups of CSS declarations that you want to reuse. They can even accept arguments like functions!" },
        { feature: "Partials (_name.scss)", purpose: "Modular Architecture", description: "Small, isolated SCSS files. The underscore tells the compiler not to generate a standalone CSS file for it." },
        { feature: "Modules (@use)", purpose: "Namespaced Imports", description: "Loads Partials into your main file. Replaces the old '@import' to prevent variable name collisions." },
    ], []);

    // Coding examples demonstrating SCSS superpowers
    const codingModules = useMemo(() => [
        {
            title: "1. Variables & Nesting",
            code: `// SCSS Input\n$primary-color: goldenrod;\n$bg-dark: #100720;\n\n.navbar {\n  background-color: $bg-dark;\n  padding: 1rem;\n\n  // Nesting prevents writing '.navbar .nav-link' over and over\n  .nav-link {\n    color: white;\n    text-decoration: none;\n\n    // The '&' refers to the parent selector (.nav-link)\n    &:hover {\n      color: $primary-color;\n    }\n  }\n}`,
            desc: "Variables keep your branding consistent. Nesting makes your stylesheets much easier to read by mimicking the DOM structure. The `&` symbol is incredibly powerful for targeting pseudo-classes like `:hover` or `:focus`."
        },
        {
            title: "2. Mixins: Don't Repeat Yourself (DRY)",
            code: `// Define the Mixin (Think of it as a function)\n@mixin flex-center($direction: row) {\n  display: flex;\n  flex-direction: $direction;\n  justify-content: center;\n  align-items: center;\n}\n\n// Use the Mixin\n.hero-section {\n  // Calling it without arguments uses the default (row)\n  @include flex-center();\n  height: 100vh;\n}\n\n.card-column {\n  // Passing an argument to change behavior\n  @include flex-center(column);\n  padding: 2rem;\n}`,
            desc: "If you find yourself writing the exact same 4 lines of CSS to center items everywhere, put them in a `@mixin`. You can `include` it anywhere, vastly reducing the size of your codebase."
        },
        {
            title: "3. Architecture: Partials and @use",
            code: `// 📁 _variables.scss (The underscore makes it a Partial)\n$brand-gold: #DAA520;\n$brand-dark: #100720;\n\n// --------------------------------------------------\n\n// 📁 main.scss (The entry point)\n// Import the variables module\n@use 'variables' as vars;\n\n.cta-button {\n  // Access variables using the namespace 'vars'\n  background-color: vars.$brand-gold;\n  color: vars.$brand-dark;\n  border-radius: 8px;\n}`,
            desc: "Never write all your CSS in one giant file. Split it into 'Partials' (e.g., `_buttons.scss`, `_layout.scss`). Use `@use` to bring them together. The namespace (`vars.`) ensures you never accidentally overwrite variables from other files."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    SCSS PREPROCESSOR
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Programming Your Styles
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Browsers only understand standard CSS. A preprocessor like SCSS (Sassy CSS) allows you to write code with programmatic features—like variables, nesting, and reusable functions—and then <strong>compiles</strong> that code into standard CSS for the browser to read. It transforms CSS from a repetitive chore into a scalable, maintainable engineering system.
                        </Typography>

                        {/* ✅ SCSS Features Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Core SCSS Superpowers</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Feature</div>
                                    <div>Primary Purpose</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {scssFeatures.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.feature}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.purpose}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/css-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
